import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';

import useSession from 'core/hooks/use-session';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { withToast } from 'core/components/toast';
import { useStore } from 'core/store/store.mobx';
import { Tooltip } from 'core/components/tooltip';
import LockIcon from 'core/assets/svg/ic-locked.svg';
import shareIcon from 'core/assets/svg/ic-share.svg';
import useStyles from '../footer.styles';
import { ConfirmCheckout } from '../../checkout';
import OfferSharingOptions from '../../offer-sharing-options';
import useActivePolicyMatch from 'offer/hooks/use-active-policy-match';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import TroubleshootingToggle from './troubleshooting-toggle';

const checkoutButtonLabel = 'Continue to Checkout';

const Checkout = observer(({ showValidationErrorsModal, onCheckout, copyLink, hasInvalidVins, loading }) => {
  const { offer: store } = useStore();
  const classes = useStyles();
  const { validateForm } = useFormikContext();
  const buttonLabelValue = hasInvalidVins ? 'Validate VIN(s)' : checkoutButtonLabel;
  const session = useSession();
  const licensedToCheckout = store.getIsLicensedForState(session) && buttonLabelValue === checkoutButtonLabel;
  const checkActivePolicyMatch = useActivePolicyMatch();
  const [sharingModal, setSharingModal] = useState({ open: false, activePolicyMatch: null });
  const [checkingActivePolicy, setCheckingActivePolicy] = useState(false);

  useEffect(() => {
    if (showValidationErrorsModal) {
      setSharingModal((current) => ({ ...current, open: false }));
    }
  }, [showValidationErrorsModal]);

  // The active-policy callout is decided before the modal opens so it never pops in afterwards.
  const openSharingOptions = async () => {
    setCheckingActivePolicy(true);
    try {
      const activePolicyMatch = await checkActivePolicyMatch(getQuoteFriendlySelectedOption(store.selectedOption));
      setSharingModal({ open: true, activePolicyMatch });
    } finally {
      setCheckingActivePolicy(false);
    }
  };

  const canShare = !store.softRejectionOnSelectedOption && licensedToCheckout;

  return (
    <>
      <div>
        <Label className={classes.title} type="subtitle">
          Your Offer is Ready
        </Label>
        <div className={classes.copyLinksContainer}>
          {canShare ? (
            <>
              <Label type="infoValue">Want to share with the applicant?</Label>
              <Button
                color="secondary"
                variant="text"
                className={classes.sharingOptionsButton}
                onClick={openSharingOptions}
                loading={checkingActivePolicy}
                disabled={checkingActivePolicy}
              >
                <img src={shareIcon} alt="" className={classes.shareIcon} /> Offer sharing options
              </Button>
            </>
          ) : (
            <div className={classes.tooltip}>
              <Tooltip
                text={
                  store.softRejectionOnSelectedOption
                    ? 'The selected policy type cannot be shared because it has rejections. See the offer rejections section for more information.'
                    : 'You must be licensed in this state to copy the offer link.'
                }
                placement="top"
                label="Offer sharing options"
                className={classes.tooltipText}
              />
              <img src={LockIcon} alt="Lock Icon" />
            </div>
          )}
        </div>
        <OfferSharingOptions
          open={sharingModal.open}
          activePolicyMatch={sharingModal.activePolicyMatch}
          onClose={() => setSharingModal({ open: false, activePolicyMatch: null })}
          copyLink={copyLink}
          loading={loading}
        />
      </div>
      <ConfirmCheckout
        onCheckout={onCheckout}
        showValidationModal={showValidationErrorsModal}
        validateCheckout={validateForm}
        CheckOutAction={({ onClick }) => (
          <Grid container alignItems="center" justifyContent="center">
            <TroubleshootingToggle />
            <Button
              variant="contained"
              color="primary"
              className={classes.btn}
              disabled={store.loading || (!hasInvalidVins && !licensedToCheckout)}
              onClick={onClick}
            >
              <Label className={classes.btnLabel}>{buttonLabelValue}</Label>
            </Button>
            {!licensedToCheckout && (
              <Tooltip text="You must be licensed in this state to bind this policy" placement="top" />
            )}
          </Grid>
        )}
      />
    </>
  );
});
export default withToast(Checkout);
